import { useTranslation } from 'react-i18next'
import { Copy, LoaderCircle, RotateCcw, Save, Trash, TriangleAlert } from 'lucide-react'
import { Button, cn } from '@ludus/ui'
import { NODE_TYPES, nodeRole, type FlowGraph, type NodeType } from '@/features/flows/types.ts'
import { nodeTypeKey } from './useNodeLabels.ts'
import { useFlowEditorState } from './useFlowEditorState.ts'

/**
 * The canvas's own controls: add, duplicate, delete, discard, save.
 *
 * **The palette is generated from `NODE_TYPES`**, not written out, so a fifth
 * node type appears here the day it has a spec — the same closed-registry
 * property the engine's handler map has. Two entries are disabled rather than
 * hidden when the graph already holds their role, because the reason ("a flow
 * has exactly one trigger", "at most one action") is worth showing where the
 * button is instead of after a refused save.
 *
 * Every control is a button, which is what makes the keyboard equivalents of
 * add, duplicate and delete free; the gestures that have no button — move and
 * connect — carry theirs on the node card itself.
 */

interface CanvasToolbarProps {
  draft: FlowGraph
  dirty: boolean
  saving: boolean
  /** How many issues the draft has, counted by core's validator. */
  issues: number
  onSave: () => void
  onDiscard: () => void
}

export function CanvasToolbar({
  draft,
  dirty,
  saving,
  issues,
  onSave,
  onDiscard,
}: CanvasToolbarProps) {
  const { t } = useTranslation()
  const selectedNodeId = useFlowEditorState((s) => s.selectedNodeId)
  const addNode = useFlowEditorState((s) => s.addNode)
  const deleteNode = useFlowEditorState((s) => s.deleteNode)
  const duplicateNode = useFlowEditorState((s) => s.duplicateNode)

  const selected = draft.nodes.find((n) => n.id === selectedNodeId) ?? null
  const selectedIsTrigger = selected !== null && nodeRole(selected.type) === 'trigger'

  const hasRole = (type: NodeType): boolean =>
    draft.nodes.some((n) => nodeRole(n.type) === nodeRole(type))

  /** Trigger and action are capped at one each; a condition is not capped. */
  const capped = (type: NodeType): boolean => {
    const role = nodeRole(type)
    return (role === 'trigger' || role === 'action') && hasRole(type)
  }

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-t-xl border border-b-0 border-border bg-background-secondary px-2 py-1.5">
      <span className="pl-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted">
        {t('pages.flows.canvas.add', { defaultValue: 'Add' })}
      </span>
      {NODE_TYPES.map((type) => (
        <Button
          key={type}
          variant="outline"
          size="sm"
          disabled={capped(type)}
          onClick={() => addNode(type)}
          title={
            capped(type)
              ? t(`pages.flows.canvas.capped.${nodeRole(type)}`, {
                  defaultValue: 'This flow already has one.',
                })
              : undefined
          }
          className="text-xs"
        >
          {t(`pages.flows.canvas.type.${nodeTypeKey(type)}`, { defaultValue: type })}
        </Button>
      ))}

      <span className="mx-1 hidden h-5 w-px bg-border @md/flow:block" aria-hidden="true" />

      <Button
        variant="ghost"
        size="sm"
        // The same cap the Add palette applies. `capped` asks whether the graph
        // already holds this role, which for the selected node's own type is
        // true exactly when that role is capped — so a trigger and an action
        // are never duplicable, and a condition always is. Without this,
        // Duplicate was a second door onto a second trigger and the resulting
        // graph could not be saved at all.
        disabled={selected === null || capped(selected.type)}
        onClick={() => selected && duplicateNode(selected.id)}
        title={
          selected !== null && capped(selected.type)
            ? t(`pages.flows.canvas.capped.${nodeRole(selected.type)}`, {
                defaultValue: 'This flow already has one.',
              })
            : undefined
        }
        className="text-xs"
      >
        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
        <span className="hidden @md/flow:inline">
          {t('pages.flows.canvas.duplicate', { defaultValue: 'Duplicate' })}
        </span>
      </Button>
      <Button
        variant="ghost"
        size="sm"
        disabled={selected === null || selectedIsTrigger}
        onClick={() => selected && deleteNode(selected.id)}
        className="text-xs"
      >
        <Trash className="h-3.5 w-3.5" aria-hidden="true" />
        <span className="hidden @md/flow:inline">
          {t('pages.flows.canvas.delete', { defaultValue: 'Delete' })}
        </span>
      </Button>

      <div className="ml-auto flex items-center gap-2">
        {issues > 0 && (
          <span
            className={cn(
              'flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px]',
              'bg-toast-warning/60 text-toast-warning-text',
            )}
          >
            <TriangleAlert className="h-3.5 w-3.5" aria-hidden="true" />
            {t('pages.flows.canvas.issues', {
              count: issues,
              defaultValue: '{{count}} to fix before it can be saved',
            })}
          </span>
        )}
        <Button
          variant="ghost"
          size="sm"
          disabled={!dirty || saving}
          onClick={onDiscard}
          className="text-xs"
        >
          <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="hidden @md/flow:inline">
            {t('pages.flows.canvas.discard', { defaultValue: 'Discard' })}
          </span>
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={!dirty || saving}
          onClick={onSave}
          className="text-xs"
        >
          {saving ? (
            <LoaderCircle className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <Save className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {t('pages.flows.canvas.save', { defaultValue: 'Save' })}
        </Button>
      </div>
    </div>
  )
}
